import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppContext } from "../settings/SettingsActions";
import { AppContext, AppMode, LoginResponseStatus, UserInfo } from "../http/protocol";
import useGlifeCommunication, { USER_DID_NOT_CONSENT_DATA_SHARING_CODE } from "../glife/useGlifeCommunication";
import { fetchUserInfo, loginResponse, setAppMode } from "../user/UserActions";
import { useHistory } from "react-router";
import { getLoginPromptParams } from "../utils/utils";
import { ErrorPageTypes } from "../errorpage/ErrorPage";
import OptModal from "./OptModal";
import { useGetQueryParamValues } from "../onboardingpage/modalview/utils";
import { LOCALSTORAGE_APP_CONTEXT, LOCALSTORAGE_APP_MODE } from "./useGlifeCheckContextAndMode";

function GlifeView() {
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo>({});
  const dispatch = useDispatch();
  const history = useHistory();
  const [overrideAuthCode] = useGetQueryParamValues(["overrideAuthCode"])();

  const handleUserLogin = useCallback(
    (userInfo: UserInfo) => {
      const isLoginSuccess = userInfo.responseStatus === LoginResponseStatus.SUCCESS;
      const shouldPromptLogin = userInfo.responseStatus === LoginResponseStatus.THIRD_PARTY_SSO_USER_EXIST;

      if (isLoginSuccess) {
        dispatch(loginResponse(userInfo));
        dispatch(fetchUserInfo(true) as any);
        history.replace("/glife-landingpage");
      }
      if (shouldPromptLogin) {
        history.replace(`/glife/login${getLoginPromptParams(userInfo.email, userInfo.phoneNumber, userInfo.verificationChannel)}`);
      }
    },
    [dispatch, history],
  );
  const successCallbackSendAuthCode = useCallback(
    (userInfo: UserInfo) => {
      if (!userInfo) {
        history.replace(`/error-page/${ErrorPageTypes.GLIFE}`);
        return;
      }

      setUserInfo(userInfo);

      if (userInfo.blocked) {
        history.replace(`/error-page/${ErrorPageTypes.USER_BLOCKED}`);
        return;
      }
      if (userInfo.attributes?.["glifeHasConsented"] === "false") {
        setShowOtpModal(true);
        return;
      }
      handleUserLogin(userInfo);
    },
    [handleUserLogin, history],
  );
  const errorCallbackSendAuthCode = useCallback(
    (error: Error | any) => {
      if (error?.message === USER_DID_NOT_CONSENT_DATA_SHARING_CODE) {
        history.replace(`/error-page/${ErrorPageTypes.GLIFE_USER_DID_NOT_CONSENT_DATA_SHARING}`);
        return;
      }
      if (error?.response?.status === 406) {
        history.replace(`/error-page/${ErrorPageTypes.GLIFE_UNDER_AGE}`);
        return;
      }
      history.replace(`/error-page/${ErrorPageTypes.GLIFE}`);
    },
    [history],
  );
  useGlifeCommunication(successCallbackSendAuthCode, errorCallbackSendAuthCode, overrideAuthCode);

  useEffect(() => {
    dispatch(setAppContext(AppContext.GLIFE));
    dispatch(setAppMode(AppMode.EMBEDDED));
    localStorage.setItem(LOCALSTORAGE_APP_CONTEXT, AppContext.GLIFE);
    localStorage.setItem(LOCALSTORAGE_APP_MODE, AppMode.EMBEDDED);
  }, [dispatch]);

  const hideModal = () => {
    setShowOtpModal(false);
  };

  return (
    <>
      <OptModal show={showOtpModal} hide={hideModal} handleUserLogin={() => handleUserLogin(userInfo)} />
      {!showOtpModal && <FullPageLoading />}
    </>
  );
}

const fullPageLoadingStyle: React.CSSProperties = { position: "fixed", top: "0", left: "0", margin: "0", padding: "20vh 0", zIndex: "99999", background: "var(--main-background)", height: "100vh" };

export const FullPageLoading = () => (
  <div className="js-loader" style={fullPageLoadingStyle}>
    <img src="/preloader.png" id="site-loader" width="64px" alt="loading spinner" />
  </div>
);

export default GlifeView;


